import { get, curry } from 'lodash'
import type { FieldSpec } from '@wix/wix-data-schema-types'
import {
  CMS_FIELD_PLUGIN_ID,
  CATEGORY_FIELD_PLUGIN_ID,
  PRIMARY_CATEGORY_FIELD_PLUGIN_ID,
  FieldType,
} from '@wix/wix-data-schema-types'
import { getReferencingField } from './fieldSelectors'
import { getFieldFromReferencedCollectionName } from './referenceFieldPath'
import { isFieldReferencedOrNested } from './isFieldReferencedOrNested'
import type { DigestedSchemaField } from '@wix/wix-data-client-common-standalone'
const {
  multiReference,
  reference,
  number,
  document,
  pageLink,
  dateTime,
  date,
  time,
  stringArray,
  boolean,
  url,
  object,
  slug,
} = FieldType

export type ExtendedFieldSpec = FieldSpec &
  Pick<DigestedSchemaField, 'name' | 'isNested'>

const getFieldName = (field: ExtendedFieldSpec) => get(field, 'name')

export const getFieldType = (field: FieldSpec) => get(field, 'type')

export const isSystemField = (field: FieldSpec) => !!field.systemField
export const isDeletedField = (field: FieldSpec | undefined): boolean =>
  !field || !!field.isDeleted

export const fieldNameMatches = (field: ExtendedFieldSpec, name: string) => {
  const fieldName = getFieldName(field)
  return Boolean(
    fieldName === name ||
    (isFieldFromReferencedCollection(field) &&
      getFieldFromReferencedCollectionName(fieldName) === name),
  )
}

export const isOwnerField = (field: ExtendedFieldSpec) => {
  if (!field || !isSystemField(field)) {
    return false
  }
  return fieldNameMatches(field, '_owner')
}

const isOfFieldType = curry(
  (fieldType: FieldType, field: FieldSpec) => getFieldType(field) === fieldType,
)
export const isMultiReferenceField = isOfFieldType(multiReference)

export const isReferenceField = isOfFieldType(reference)

export const isReferencingField = (field: FieldSpec) =>
  isOfFieldType(reference, field) || isOfFieldType(multiReference, field)

export const isNumberField = isOfFieldType(number)
export const isDocumentField = isOfFieldType(document)
export const isPagelinkField = isOfFieldType(pageLink)
export const isDatetimeField = isOfFieldType(dateTime)
export const isDateField = isOfFieldType(date)
export const isTimeField = isOfFieldType(time)
export const isStringArrayField = isOfFieldType(stringArray)
export const isBooleanField = isOfFieldType(boolean)
export const isUrlField = isOfFieldType(url)
export const isSlugField = isOfFieldType(slug)

export const isParentMultiRef = (
  fields: DigestedSchemaField[],
  field: DigestedSchemaField,
) =>
  getReferencingField(fields, field.name)
    .map((parentField: DigestedSchemaField) =>
      isMultiReferenceField(parentField),
    )
    .getOrElse(false)

export const isFieldNested = (field?: Pick<DigestedSchemaField, 'isNested'>) =>
  Boolean(field?.isNested)

export const isFieldFromReferencedCollection = (field?: ExtendedFieldSpec) =>
  !!field && isFieldReferencedOrNested(field.name) && !isFieldNested(field)

export const isParentField = (fieldType: FieldType) =>
  [reference, object].includes(fieldType)

export const isCategoryField = (field: FieldSpec) =>
  !!field.plugins?.[CATEGORY_FIELD_PLUGIN_ID]

export const isPrimaryCategoryField = (field: FieldSpec) =>
  !!field.plugins?.[PRIMARY_CATEGORY_FIELD_PLUGIN_ID]

export const isCMSInternalField = (field: DigestedSchemaField | FieldSpec) =>
  field.plugins?.[CMS_FIELD_PLUGIN_ID]?.internal
